import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { DirectionsCar as CarIcon } from '@mui/icons-material';
import type { Technika } from '../api/JsdhNotifikaceService/generated/models/Technika';
import type { PoplachTechnikaResponse } from '../api/JsdhNotifikaceService/generated/models/PoplachTechnika';
import { splitEmailText } from '../utils/technikaParser';
import TechnikaFoto from './TechnikaFoto';
import { FitHeightBox } from './FitHeightBox';
import type { Orientace } from '../hooks/useOrientaceKontejneru';

// Karta může být buď spárovaná s DB (s plnými daty) nebo unmatched z emailu (pouze text).
// Obě varianty se renderují stejným layoutem; unmatched nemá foto/SPZ/poznámku, jen
// volací znak + název + KOPIS chip + placeholder ikonu auta.
export type TechnikaCard =
    | { type: 'matched'; technika: Technika; source?: PoplachTechnikaResponse }
    | { type: 'unmatched'; text: string };

interface Vyjezd2026TechnikaSectionProps {
    cards: TechnikaCard[];
    deviceToken?: string;
    /** Mřížka karet: 'sirka' (výchozí) nebo 'vyska' (kiosek na výšku, kap. 3.2 specu). */
    mrizka?: Orientace;
}

const MAX_TECHNIKA = 6;

// Layout dle počtu karet: 1-3 jedna řada, 4 jako 2×2, 5-6 jako 3×2.
// Grid s řádky minmax(0, 1fr) má pevný strop výšky: karta nikdy nepřeroste sekci
// (u flex-wrap řádky rostly podle obsahu a poznámka končila pod okrajem monitoru).
const gridLayout = (count: number): { columns: number; rows: number } => {
    if (count <= 1) return { columns: 2, rows: 1 }; // 1 vůz: polovina šířky, ne přes celou sekci
    if (count === 2) return { columns: 2, rows: 1 };
    if (count === 3) return { columns: 3, rows: 1 };
    if (count === 4) return { columns: 2, rows: 2 };
    return { columns: 3, rows: 2 }; // 5 nebo 6
};

// Na výšku (spec kiosek na výšku, kap. 3.2): 1 vůz polovina šířky, 2 po polovině, 3 po třetině,
// 4 jako 2 × 2, 5–6 jako 3 + 2 / 3 + 3. Dnes se shoduje se šířkou; drží se zvlášť, aby úprava
// rozložení na šířku tiše nezměnila monitor na výšku.
export const gridLayoutNaVysku = (count: number): { columns: number; rows: number } => {
    if (count <= 2) return { columns: 2, rows: 1 };
    if (count === 3) return { columns: 3, rows: 1 };
    if (count === 4) return { columns: 2, rows: 2 };
    return { columns: 3, rows: 2 };
};

// Základ písma karty (název vozu) v px; volací znak je 1.8em. FitHeightBox ho zmenšuje podle
// místa v kartě, nejvýš na 16 px (čitelnost ze 3 m). Pod tím dostane text náznak „…".
const zakladFontPx = (count: number): number => {
    if (count <= 2) return 22;
    if (count === 3) return 19;
    if (count === 4) return 20;
    return 18; // 5-6
};

// Poznámka velitele: max. písmo; zmenšuje se nejvýš na 16 px.
const poznamkaFontPx = (count: number): number => (count <= 2 ? 22 : 19);

const textSloupecSx = {
    flex: '1 1 0', bgcolor: 'background.paper',
    // safe center: při přetečení obsah neuteče nahoru (tam ho scrollHeight nepozná a FitHeightBox
    // by písmo nezmenšil), ale dolů, kde se přetečení změří.
    display: 'flex', flexDirection: 'column', justifyContent: 'safe center',
} as const;

const volaciSx = {
    // nowrap bez ellipsis: přetečení do šířky pozná FitHeightBox a zmenší písmo celého sloupce
    // (dřív nowrap+ellipsis uřízl „CAS 30" na „CAS…" bez možnosti zmenšení).
    fontSize: '1.8em', fontWeight: 900, color: 'error.main', lineHeight: 1.1, whiteSpace: 'nowrap',
} as const;

const nazevSx = { fontSize: '1em', fontWeight: 600, color: 'text.primary', lineHeight: 1.25, overflowWrap: 'break-word' } as const;

const chipSx = (bgcolor: string) => ({
    height: 'auto', fontWeight: 700, fontSize: 'max(16px, 0.8em)', color: '#fff', bgcolor,
    '& .MuiChip-label': { px: 0.75 },
});

const fotoSx = {
    height: '100%', maxHeight: '100%', aspectRatio: '4/3', maxWidth: '45%', minWidth: 0,
    flex: '0 1 auto', alignSelf: 'center',
} as const;

export const Vyjezd2026TechnikaSection: React.FC<Vyjezd2026TechnikaSectionProps> = ({
    cards, deviceToken, mrizka = 'sirka',
}) => {
    const visible = cards.slice(0, MAX_TECHNIKA);
    const overflow = Math.max(0, cards.length - MAX_TECHNIKA);
    const visibleCount = visible.length;
    const { columns, rows } = mrizka === 'vyska' ? gridLayoutNaVysku(visibleCount) : gridLayout(visibleCount);

    if (visibleCount === 0) {
        return (
            <Box sx={{
                flex: 1, p: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 1,
            }}>
                <CarIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'text.secondary' }}>
                    Žádná technika nepovolána
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ flex: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 0 }}>
            {overflow > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <Typography data-testid="technika-dalsi" sx={{ fontSize: '1rem', color: 'text.secondary', fontStyle: 'italic' }}>
                        + {overflow} dalších
                    </Typography>
                </Box>
            )}
            <Box data-testid="technika-grid" data-mrizka={mrizka} sx={{
                display: 'grid', gap: 1, flex: 1, minHeight: 0,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
            }}>
                {visible.map((card, idx) => {
                    if (card.type === 'matched') {
                        const t = card.technika;
                        const source = card.source;
                        const poznamka = source?.zdroj === 'VELITEL' ? source.poznamka?.trim() : '';
                        return (
                            <Paper key={`m-${t.id}`} data-testid="technika-karta" elevation={1} sx={{
                                p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5,
                                minWidth: 0, minHeight: 0, overflow: 'hidden',
                            }}>
                                <Box sx={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 1 }}>
                                    <FitHeightBox data-testid="technika-text" maxFontPx={zakladFontPx(visibleCount)}
                                        fitKey={`${t.volaciZnak}|${t.nazev}|${t.spz}|${source?.zdroj}|${poznamka}`} sx={textSloupecSx}>
                                        <Typography data-testid="technika-volaci" sx={volaciSx}>{t.volaciZnak}</Typography>
                                        <Typography sx={nazevSx}>{t.nazev}</Typography>
                                        {(t.spz || source) && (
                                            // SPZ a zdroj na jednom řádku: o řádek nižší karta, nic pod poznámkou.
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.25, mt: 0.25 }}>
                                                {t.spz && (
                                                    <Typography sx={{ fontSize: 'max(16px, 0.85em)', color: 'text.secondary', lineHeight: 1.25 }}>
                                                        {t.spz}
                                                    </Typography>
                                                )}
                                                {source && (
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            source.zdroj === 'KOPIS'
                                                                ? 'KOPIS'
                                                                : source.potvrzenoKopis
                                                                    ? 'Velitel + KOPIS'
                                                                    : 'Velitel'
                                                        }
                                                        sx={chipSx(source.zdroj === 'KOPIS' ? 'info.main' : source.potvrzenoKopis ? 'success.main' : 'warning.main')}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </FitHeightBox>
                                    <TechnikaFoto
                                        technikaId={t.id}
                                        maFotografii={t.maFotografii}
                                        alt={t.nazev}
                                        deviceToken={deviceToken}
                                        objectFit="cover"
                                        ikonaFontSize={visibleCount >= 3 ? 64 : 96}
                                        sx={fotoSx}
                                    />
                                </Box>
                                {/* Poznámka velitele: v toku pod obsahem karty (ne absolutní překryv přes SPZ a chip).
                                    Má strop poloviny karty; písmo se zmenšuje do 16 px, pak náznak „…". */}
                                {poznamka && (
                                    <FitHeightBox data-testid="technika-poznamka"
                                        maxFontPx={poznamkaFontPx(visibleCount)} fitKey={poznamka} orezRadky
                                        sx={{
                                            flex: '0 1 auto', maxHeight: '50%',
                                            bgcolor: 'error.main', color: '#fff', borderRadius: 0.5,
                                            px: 1, py: 0.5,
                                            fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word',
                                        }}>
                                        {poznamka}
                                    </FitHeightBox>
                                )}
                            </Paper>
                        );
                    }
                    // unmatched — fallback karta s daty z emailu, vizuálně shodná s matched KOPIS.
                    // Bez tooltipu (kiosek bez myši), bez foto/SPZ/poznámky, žádné výstražné prvky.
                    const { volaciZnak, nazev } = splitEmailText(card.text);
                    return (
                        <Paper key={`u-${idx}`} data-testid="technika-karta" elevation={1} sx={{
                            p: 0.75, display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 1,
                            minWidth: 0, minHeight: 0, overflow: 'hidden',
                        }}>
                            <FitHeightBox data-testid="technika-text" maxFontPx={zakladFontPx(visibleCount)} fitKey={card.text} sx={textSloupecSx}>
                                <Typography data-testid="technika-volaci" sx={volaciSx}>{volaciZnak}</Typography>
                                {nazev && <Typography sx={nazevSx}>{nazev}</Typography>}
                                <Box sx={{ mt: 0.25 }}>
                                    <Chip size="small" label="KOPIS" sx={chipSx('info.main')} />
                                </Box>
                            </FitHeightBox>
                            <Box sx={{
                                ...fotoSx,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: 'action.hover', borderRadius: 1,
                            }}>
                                <CarIcon sx={{ fontSize: visibleCount >= 3 ? 64 : 96, color: 'text.disabled' }} />
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};
